import axios, { AxiosResponse, AxiosInstance, AxiosTransformer } from 'axios'
import store from './store';
import Vue from 'vue';
import settings from './settings.json';


const VUE_APP_SERVER_API_URL_PREFIX_TO_USE: string = process.env.VUE_APP_SERVER_API_URL_PREFIX_TO_USE   // e.g. https://storycircle.co or http://localhost:3001
const VUE_APP_WEB_URL_PREFIX_TO_USE: string = process.env.VUE_APP_WEB_URL_PREFIX_TO_USE   // e.g. https://storycircle.co or http://localhost:8080
if (!VUE_APP_SERVER_API_URL_PREFIX_TO_USE || !VUE_APP_WEB_URL_PREFIX_TO_USE) {
  const e = 'VUE_APP_SERVER_API_URL_PREFIX_TO_USE or VUE_APP_WEB_URL_PREFIX_TO_USE is null from environment (check you .env and .env.local)'
  console.error(e)
  throw(e)
}

// "globals"
let AV: number = null

export function getURLprefix(): string {
  return VUE_APP_SERVER_API_URL_PREFIX_TO_USE
}

export function getWebURLprefix(): string {
  return VUE_APP_WEB_URL_PREFIX_TO_USE
}

// add this to all Axios get calls so that ISO-formatted dates get transformed into Date objects
// this is the complement of express's reviver thing where I use a dateReviver
const dateTransformer: any = {
  transformResponse: (axios.defaults.transformResponse as AxiosTransformer[]).concat(transformAllDatesRecursively)
}

// my own invention
function transformAllDatesRecursively(obj: any, headers: any): any {
  if (obj === null) return obj

  if (typeof obj !== 'object') {
    // if (typeof obj === 'string' && (/^\d\d\d\d-\d\d-\d\dT\d\d:\d\d:\d\d.\d\d\dZ$/.test(obj))) return new Date(obj)
    if (typeof obj === 'string' && (/^\d\d\d\d-\d+-\d+T\d+:\d+:\d+.\d+Z$/.test(obj))) return new Date(obj)   // more flexible, needed for ES results
    if (typeof obj === 'string' && (/^\d\d\d\d-\d+-\d+T\d+:\d+:\d+Z$/.test(obj))) return new Date(obj)       // more flexible, needed for ES results
    return obj      // base case
  }

  if (Array.isArray(obj)) {
    // array
    for (let i = 0; i < obj.length; i++) {
      obj[i] = transformAllDatesRecursively(obj[i], headers)
    }
  } else {
    // dictionary
    const keys: string[] = Object.keys(obj)
    for (const k of keys) {
      obj[k] = transformAllDatesRecursively(obj[k], headers)
    }
  }
  return obj
}

export function isClientOld(): boolean {
  return AV && (AV > settings.AV)
}

function getAuthenticatedHeader(): object {
  const header: any = {
    AV: settings.AV
  }
  // note, setting header values to null ends up transmitting them as "[object Object]" so I just don't add them at all if null
  if (store.state.user) header.username = store.state.user.username
  if (store.state.sessionId) header.sessionId = store.state.sessionId
  return header
}

export async function makeGetCall(url: string): Promise<any> {
  const axiosInstance: AxiosInstance = axios.create({
    timeout: 60000,
    headers: getAuthenticatedHeader()
  })
  if (store.state.devmode) console.log(`makeGetCall(${url})`)
  const response: any = await axiosInstance.get(url, dateTransformer)
  AV = Number(response.headers.AV || response.headers.av)
  return response
}

export async function makeGetCallForBinaryData(url: string): Promise<any> {
  const axiosInstance: AxiosInstance = axios.create({
    timeout: 60000,
    headers: getAuthenticatedHeader(),
    responseType: 'arraybuffer'
  })
  if (store.state.devmode) console.log(`makeGetCall(${url})`)
  const response: any = await axiosInstance.get(url, dateTransformer)
  AV = Number(response.headers.AV || response.headers.av)
  return response
}

export async function makePostCall(url: string, data: object): Promise<any> {
  const axiosInstance: AxiosInstance = axios.create({
    timeout: 60000,
    headers: getAuthenticatedHeader()
  })
  if (store.state.devmode) console.log(`makePostCall(${url})`)
  if (store.state.devmode) console.dir(data)
  const response: any = await axiosInstance.post(url, data, dateTransformer)
  AV = Number(response.headers.AV || response.headers.av)
  return response
}

export async function makePutCall(url: string, data: object): Promise<any> {
  const axiosInstance: AxiosInstance = axios.create({
    timeout: 60000,
    headers: getAuthenticatedHeader()
  })
  if (store.state.devmode) console.log(`makePutCall(${url})`)
  const response: any = await axiosInstance.put(url, data, dateTransformer)
  AV = Number(response.headers.AV || response.headers.av)
  return response
}

export function getLuma(c: string): number {
  if (c.startsWith('#')) c = c.substring(1)
  const rgb: number = parseInt(c, 16);   // convert rrggbb to decimal
  // tslint:disable-next-line: no-bitwise
  const r: number = (rgb >> 16) & 0xff;  // extract red
  // tslint:disable-next-line: no-bitwise
  const g: number = (rgb >> 8) & 0xff;  // extract green
  // tslint:disable-next-line: no-bitwise
  const b: number = (rgb >> 0) & 0xff;  // extract blue

  const luma: number = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709, and will be 0-255
  return luma;
}

export function toast(v: Vue, title: string, msg: string): void {
  const titleLower: string = title.toLocaleLowerCase()
  let variant: string = 'info'                                    // blue
  if (titleLower.includes('error')) variant = 'danger'            // red
  else if (titleLower.includes('missing')) variant = 'danger'     // red
  else if (titleLower.includes('invalid')) variant = 'danger'     // red
  else if (titleLower.includes('success')) variant = 'success'    // green
  else if (titleLower.includes('warning')) variant = 'warning'    // orange

  // do v.$root.$bvToast instead of v.$bvToast so that toast persists across router changes
  v.$root.$bvToast.toast(msg, { title, variant, solid: true, appendToast: true, noCloseButton: false, autoHideDelay: 3500 })
}

export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}
export function isValidPassword(password: string): boolean {
  return password && password.length >= 4
}
export function isValidName(name: string): boolean {
  return name && name.length > 0
}
export function capitalizeFirstLetter(s: string) {
  if (typeof s !== 'string') return ''
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// eg +17179288080 to 717-928-8080
export function prettyE164toUS(phone: string): string {
  phone = phone.replace('+1', '')
  return `${phone.substring(0, 3)}-${phone.substring(3, 6)}-${phone.substring(6, 10)}`
}

// a good alternative to toast because toasts go away when use close a modal
export function showTopAlert(variant: string, message: string, secToAutoDismiss: number): void {
  store.commit("setTopAlertVariant", { topAlertVariant: variant });
  store.commit("setTopAlertMessage", { topAlertMessage: message });
  store.commit("setTopAlertShowing", { topAlertShowing: secToAutoDismiss ? secToAutoDismiss : true });
}
export function showBotAlert(variant: string, message: string, secToAutoDismiss: number): void {
  store.commit("setBotAlertVariant", { botAlertVariant: variant });
  store.commit("setBotAlertMessage", { botAlertMessage: message });
  store.commit("setBotAlertShowing", { botAlertShowing: secToAutoDismiss ? secToAutoDismiss : true });
}

// example usage: await sleep(100)
export function sleep(ms: number): Promise<void> {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

// https://gist.github.com/sindresorhus/1993156
export function stripScripts(s: string): string {
  const b: HTMLOptionElement = new Option()
  b.innerHTML = s;
  const scriptElements = b.getElementsByTagName('script')
  for (const se of scriptElements) {
    se.parentNode.removeChild(se)
  }
  return b.innerHTML
}

export function saveUTMsToLocalStorage(query: any) {
  if (!query) return
  if (query.utm_source) localStorage.setItem('utm_source', query.utm_source as string)
  if (query.utm_campaign) localStorage.setItem('utm_campaign', query.utm_campaign as string)
  if (query.utm_medium) localStorage.setItem('utm_medium', query.utm_medium as string)
  if (query.utm_content) localStorage.setItem('utm_content', query.utm_content as string)
  if (query.utm_term) localStorage.setItem('utm_term', query.utm_term as string)

  // if (query.utm_source) console.log(`Saved utm_source ${query.utm_source} to localStorage`)

}


(async () => {
})()
